import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { Pill } from '../ui/pill';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Smartphone, Download, Info, CheckCircle, Clock, FolderOpen } from 'lucide-react';
import { MobileStorageService } from '../../services/MobileStorageService';
import { detectOS } from '../../utils/pathUtils';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

interface MobileStorageGuideProps {
  onDismiss?: () => void;
  showIfNeeded?: boolean;
}

const MobileStorageGuide: React.FC<MobileStorageGuideProps> = ({ 
  onDismiss, 
  showIfNeeded = true 
}) => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const tMsg = t.mobileStorageGuide;
  const [showGuide, setShowGuide] = useState(false);
  const [mobileService] = useState(() => MobileStorageService.getInstance());
  const [currentSession, setCurrentSession] = useState(mobileService.getCurrentSessionInfo());

  useEffect(() => {
    if (showIfNeeded) {
      const isAndroid = detectOS() === 'android';
      const supportsFileSystemAPI = 'showDirectoryPicker' in window;
      const needsGuide = isAndroid && !supportsFileSystemAPI;
      
      setShowGuide(needsGuide);
    }

    const interval = setInterval(() => {
      setCurrentSession(mobileService.getCurrentSessionInfo());
    }, 1000);

    return () => clearInterval(interval);
  }, [showIfNeeded, mobileService]);

  const handleDismiss = () => {
    setShowGuide(false);
    onDismiss?.();
  };

  const openDownloadsFolder = () => {
    try {
      window.open('content://downloads/', '_blank');
    } catch (error) {
      alert(tMsg.manualCheck);
    }
  };

  if (!showGuide) return null;

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-popover">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-indigo-400" />
            {tMsg.title}
          </CardTitle>
          <CardDescription>
            {tMsg.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Storage Method Info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>{tMsg.autoDownloadTitle}</strong><br />
              {tMsg.autoDownloadDesc}
            </AlertDescription>
          </Alert>

          {/* Current Session Status */}
          {currentSession && (
            <div className="bg-indigo-500/10 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-indigo-400" />
                <span className="font-medium text-indigo-300">{tMsg.currentSession}</span>
                <Pill size="sm" variant="secondary">{currentSession.name}</Pill>
              </div>
              <div className="text-sm text-indigo-400">
                <p>• {tMsg.eegData(currentSession.eegData.length)}</p>
                <p>• {tMsg.ppgData(currentSession.ppgData.length)}</p>
                <p>• {tMsg.accData(currentSession.accData.length)}</p>
              </div>
            </div>
          )}

          {/* How it works */}
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">{tMsg.howItWorks}:</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <span>{tMsg.autoSave30s}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <span>{tMsg.autoSave50MB}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <span>{tMsg.finalSave}</span>
              </div>
              <div className="flex items-start gap-2">
                <Download className="w-4 h-4 text-indigo-400 mt-0.5" />
                <span>{tMsg.checkDownloads}</span>
              </div>
            </div>
          </div>

          {/* File Types */}
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">{tMsg.savedFiles}:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Pill size="sm" variant="outline">metadata.json</Pill>
              <Pill size="sm" variant="outline">eeg_raw.csv</Pill>
              <Pill size="sm" variant="outline">ppg_raw.csv</Pill>
              <Pill size="sm" variant="outline">acc_raw.csv</Pill>
              <Pill size="sm" variant="outline">eeg_processed.csv</Pill>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Pill
              variant="secondary"
              size="sm"
              onClick={openDownloadsFolder}
              className="flex-1"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              {tMsg.openDownloads}
            </Pill>
            <Pill
              variant="primary"
              size="sm"
              onClick={handleDismiss}
              className="flex-1"
            >
              {tMsg.confirm}
            </Pill>
          </div>

          {/* Additional Info */}
          <div className="text-xs text-muted-foreground border-t pt-3">
            <p><strong>{tMsg.note}:</strong> {tMsg.noteTimestamp}</p>
            <p>{tMsg.noteTransfer}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileStorageGuide;
